import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import channelReducer, { 
  setChannel, 
  addMessage, 
  commitPendingMessage,
  addAgentMessage,
  setAgentStatus,
  addEmail,
  setAppUrl
} from '../../features/channelSlice';
import deviceReducer from '../../features/deviceSlice';
import notificationReducer, { addNotification, dismissNotification } from '../../features/notificationSlice';
import sidePanelReducer from '../../features/sidePanelSlice';
import HomeView from '../HomeView';
import AgentView from '../AgentView';
import MessagingView from '../MessagingView';
import EmailView from '../EmailView';
import CalendarView from '../CalendarView';
import AppView from '../AppView';
import NotificationContainer from '../NotificationContainer';
import ToastNotification from '../ToastNotification';

// Create context for demo typing simulation
export const DemoTypingContext = createContext({ 
  typingText: '', 
  setTypingText: () => {} 
});

// Create context for demo toasts
export const DemoToastContext = createContext({
  toasts: [],
  addToast: () => {},
  removeToast: () => {}
});

// Helper function to create bezel gradient (same as DeviceFrame)
const createBezelGradient = (baseColor) => {
  // Handle undefined/null values with default dark color to match iPhone style
  if (!baseColor || typeof baseColor !== 'string') {
    baseColor = '#1a1a2e'; // Default dark iPhone-like color (rgb(26, 26, 46))
  }
  
  // Convert hex to RGB for calculations
  const hex = baseColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // For dark colors (like #1a1a2e), create a subtle metallic gradient
  // For lighter colors, use more dramatic variations
  const isDark = (r + g + b) / 3 < 100;
  
  if (isDark) {
    // Very subtle variations for dark bezels to match the HTML example
    // Example shows: rgb(66,66,96) -> rgb(46,46,71) -> rgb(26,26,46) -> rgb(18,18,34) -> rgb(14,14,28)
    // This means variations of roughly +40 to -12 from the base color
    const highlight = `rgb(${Math.min(255, r + 40)}, ${Math.min(255, g + 40)}, ${Math.min(255, b + 50)})`; // Top highlight
    const lightMid = `rgb(${Math.min(255, r + 20)}, ${Math.min(255, g + 20)}, ${Math.min(255, b + 25)})`; // Light transition
    const mid = `rgb(${r}, ${g}, ${b})`; // Base color (center)
    const darkMid = `rgb(${Math.max(0, r - 8)}, ${Math.max(0, g - 8)}, ${Math.max(0, b - 12)})`; // Dark transition
    const shadow = `rgb(${Math.max(0, r - 12)}, ${Math.max(0, g - 12)}, ${Math.max(0, b - 18)})`; // Bottom shadow
    
    return `linear-gradient(145deg, 
      ${highlight} 0%, 
      ${lightMid} 12%, 
      ${mid} 25%, 
      ${mid} 50%, 
      ${darkMid} 75%, 
      ${shadow} 92%, 
      ${shadow} 100%)`;
  } else {
    // More dramatic gradient for lighter bezel colors (silver, etc.)
    const highlight = `rgb(${Math.min(255, r + 60)}, ${Math.min(255, g + 60)}, ${Math.min(255, b + 60)})`; 
    const lightMid = `rgb(${Math.min(255, r + 25)}, ${Math.min(255, g + 25)}, ${Math.min(255, b + 25)})`; 
    const mid = `rgb(${r}, ${g}, ${b})`; 
    const darkMid = `rgb(${Math.max(0, r - 40)}, ${Math.max(0, g - 40)}, ${Math.max(0, b - 40)})`; 
    const shadow = `rgb(${Math.max(0, r - 60)}, ${Math.max(0, g - 60)}, ${Math.max(0, b - 60)})`; 
    
    return `linear-gradient(145deg, 
      ${highlight} 0%, 
      ${lightMid} 15%, 
      ${mid} 30%, 
      ${mid} 50%, 
      ${darkMid} 70%, 
      ${shadow} 90%, 
      ${shadow} 100%)`;
  }
};

// Helper function to create home button gradient (darker version of bezel color)
const createHomeButtonGradient = (baseColor) => {
  // Handle undefined/null values with default dark color
  if (!baseColor || typeof baseColor !== 'string') {
    baseColor = '#1a1a2e'; // Default dark iPhone-like color
  }
  
  const hex = baseColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Create darker variations for home button
  const dark1 = `rgb(${Math.max(0, r - 80)}, ${Math.max(0, g - 80)}, ${Math.max(0, b - 80)})`;
  const dark2 = `rgb(${Math.max(0, r - 100)}, ${Math.max(0, g - 100)}, ${Math.max(0, b - 100)})`;
  const dark3 = `rgb(${Math.max(0, r - 120)}, ${Math.max(0, g - 120)}, ${Math.max(0, b - 120)})`;
  
  return `linear-gradient(135deg, ${dark1} 0%, ${dark2} 50%, ${dark3} 100%)`;
};

// Create initial mock state for the demo
const createInitialState = () => ({
  channel: {
    currentChannel: 'home',
    previousChannel: null,
    channelConfig: {
      home: { title: 'Home', visible: true },
      agent: { 
        title: 'Cara',
        subtitle: 'AI Agent',
        visible: true,
        color: '#FF6B35',
        icon: 'chat',
        widgetVisible: false
      },
      sms: { 
        title: 'Messages', 
        subtitle: '+1 234 567 8900',
        visible: true,
        color: '#34C759',
        icon: 'sms'
      },
      email: { 
        title: 'Mail', 
        visible: true,
        color: '#007AFF',
        icon: 'mail'
      },
      calendar: { 
        title: 'Calendar', 
        visible: true,
        color: '#FF3B30',
        icon: 'event'
      },
      browser: { title: 'Safari', visible: true, color: '#4285F4', icon: 'public' },
      app: { title: 'Banking', visible: true, color: '#0066CC', icon: 'account_balance' },
      call: { title: 'Phone', visible: true, color: '#34C759', icon: 'phone' }
    },
    messages: [],
    pendingMessage: null,
    smsJustReceived: false,
    agentMessages: [],
    agentStatus: 'idle',
    calendarDate: new Date().toISOString().split('T')[0],
    emails: [],
    deviceBezelColor: '#1a1a2e', // Dark iPhone-like bezel color
    homeBackgroundImage: '/images/labb-dark.webp',
    unreadCounts: {},
    enableSoundEffects: false
  },
  device: {
    type: 'phone'
  },
  notifications: {
    notifications: []
  },
  sidePanel: {}
});

// Component that orchestrates the demo using real Redux actions
const DemoOrchestrator = ({ scriptUrl, onTypingTextChange, onResetStart, onChannelTransition }) => {
  const dispatch = useDispatch();
  const { addToast } = useContext(DemoToastContext);
  const [script, setScript] = useState(null);
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const timeoutRef = useRef(null);
  const typingIntervalRef = useRef(null);

  // Load script
  useEffect(() => {
    fetch(scriptUrl)
      .then(res => res.json())
      .then(data => {
        setScript(data.script);
        console.log('✅ Demo script loaded:', data.script.name);
        setIsPlaying(true);
      })
      .catch(err => console.error('❌ Failed to load demo script:', err));
  }, [scriptUrl]);

  // Process demo events
  const processEvent = (event) => {
    console.log('🎬 Processing event:', event.type);

    switch (event.type) {
      case 'notification':
        const notificationId = Date.now();
        dispatch(addNotification({
          id: notificationId,
          ...event.params
        }));
        // Auto-dismiss after 3 seconds
        setTimeout(() => {
          dispatch(dismissNotification(notificationId));
        }, 3000);
        break;

      case 'openChannel':
        if (onChannelTransition) {
          onChannelTransition(event.params.channel, 'opening');
        }
        // Small delay to let animation start before changing channel
        setTimeout(() => {
          dispatch(setChannel(event.params.channel));
        }, 50);
        break;

      case 'homeButton':
        if (onChannelTransition) {
          onChannelTransition('home', 'closing');
        }
        setTimeout(() => {
          dispatch(setChannel('home'));
        }, 50);
        break;

      case 'agentMessage':
        dispatch(setAgentStatus({ agentStatus: 'idle' }));
        dispatch(addAgentMessage({
          sender: 'agent',
          text: event.params.message,
          timestamp: Date.now()
        }));
        break;

      case 'customerTyping':
        // Clear any existing typing interval
        if (typingIntervalRef.current) {
          clearInterval(typingIntervalRef.current);
        }
        
        // Simulate typing character by character
        const text = event.params.message;
        const typingSpeed = event.params.typingSpeed || 40; // Default 40ms for double speed
        let currentText = '';
        let charIndex = 0;
        
        onTypingTextChange(''); // Clear first
        typingIntervalRef.current = setInterval(() => {
          if (charIndex < text.length) {
            currentText += text[charIndex];
            onTypingTextChange(currentText);
            charIndex++;
          } else {
            clearInterval(typingIntervalRef.current);
            typingIntervalRef.current = null;
          }
        }, typingSpeed);
        break;

      case 'customerMessage':
        // Clear typing text
        onTypingTextChange('');
        if (typingIntervalRef.current) {
          clearInterval(typingIntervalRef.current);
          typingIntervalRef.current = null;
        }
        
        dispatch(addAgentMessage({
          sender: 'user',
          text: event.params.message,
          timestamp: Date.now()
        }));
        break;

      case 'agentTyping':
        dispatch(setAgentStatus({ agentStatus: 'thinking' }));
        setTimeout(() => {
          dispatch(setAgentStatus({ agentStatus: 'idle' }));
        }, event.params.duration || 2000);
        break;

      case 'smsMessage':
        if (event.params.sender === 'agent') {
          dispatch(addMessage({
            sender: 'agent',
            text: event.params.message,
            timestamp: Date.now()
          }));
          dispatch(commitPendingMessage());
        } else {
          dispatch(addMessage({
            sender: 'me',
            text: event.params.message,
            timestamp: Date.now()
          }));
        }
        break;

      case 'email':
        dispatch(addEmail({
          subject: event.params.subject,
          body: event.params.body,
          senderName: event.params.senderName || event.params.sender,
          date: new Date().toISOString(),
          attachments: event.params.attachments || []
        }));
        break;

      case 'toast':
        addToast({
          subject: event.params.subject || event.params.title,
          body: event.params.body || event.params.message,
          icon: event.params.icon
        });
        break;

      case 'appForm':
        // Create a data URL for a mock form HTML page styled like a modern banking app
        const formHtml = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', sans-serif;
      background: linear-gradient(135deg, #0A0F1A 0%, #1C2837 50%, #2D3748 100%);
      min-height: 100vh;
      padding: 0;
      color: #fff;
      position: relative;
      overflow-x: hidden;
    }
    /* Animated background effect */
    body::before {
      content: '';
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: radial-gradient(circle at 20% 30%, rgba(68, 194, 253, 0.08) 0%, transparent 50%),
                  radial-gradient(circle at 80% 70%, rgba(139, 92, 246, 0.06) 0%, transparent 50%);
      pointer-events: none;
      z-index: 0;
    }
    .container {
      position: relative;
      z-index: 1;
      max-width: 500px;
      margin: 0 auto;
      min-height: 100vh;
      display: flex;
      flex-direction: column;
    }
    .header {
      background: linear-gradient(135deg, rgba(68, 194, 253, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      border-bottom: 1px solid rgba(68, 194, 253, 0.2);
      padding: 24px 20px;
      text-align: center;
    }
    .header h1 {
      font-size: 20px;
      font-weight: 600;
      color: #fff;
      margin-bottom: 8px;
      letter-spacing: -0.02em;
    }
    .case-badge {
      display: inline-block;
      background: rgba(68, 194, 253, 0.2);
      padding: 6px 14px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 500;
      letter-spacing: 0.3px;
      color: rgba(255,255,255,0.9);
      border: 1px solid rgba(68, 194, 253, 0.3);
    }
    .content {
      flex: 1;
      padding: 24px 20px;
    }
    .description {
      color: rgba(203, 213, 225, 0.9);
      font-size: 14px;
      line-height: 1.6;
      margin-bottom: 28px;
      padding: 16px;
      background: rgba(30, 41, 59, 0.4);
      border-radius: 12px;
      border-left: 3px solid #44C2FD;
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
    }
    .upload-area {
      border: 2px dashed rgba(68, 194, 253, 0.3);
      border-radius: 16px;
      padding: 40px 24px;
      text-align: center;
      background: rgba(30, 41, 59, 0.3);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      transition: all 0.3s ease;
      cursor: pointer;
      margin-bottom: 24px;
    }
    .upload-area:hover {
      border-color: rgba(68, 194, 253, 0.6);
      background: rgba(30, 41, 59, 0.5);
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(68, 194, 253, 0.15);
    }
    .upload-icon {
      width: 72px;
      height: 72px;
      margin: 0 auto 16px;
      background: linear-gradient(135deg, #44C2FD 0%, #8B5CF6 100%);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 36px;
      box-shadow: 0 8px 24px rgba(68, 194, 253, 0.3);
    }
    .upload-text {
      color: #fff;
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 8px;
      letter-spacing: -0.01em;
    }
    .upload-hint {
      color: rgba(203, 213, 225, 0.7);
      font-size: 13px;
    }
    .file-uploaded {
      background: rgba(34, 197, 94, 0.15);
      border: 2px solid rgba(134, 239, 172, 0.4);
      padding: 18px;
      border-radius: 14px;
      display: flex;
      align-items: center;
      gap: 14px;
      margin-bottom: 24px;
      animation: slideIn 0.4s ease-out;
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
    }
    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateY(-10px) scale(0.95);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }
    .file-icon {
      width: 52px;
      height: 52px;
      background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 26px;
      flex-shrink: 0;
      box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
    }
    .file-info {
      flex: 1;
      text-align: left;
    }
    .file-name {
      color: #fff;
      font-weight: 600;
      font-size: 14px;
      margin-bottom: 6px;
      letter-spacing: -0.01em;
    }
    .file-size {
      color: rgba(134, 239, 172, 0.9);
      font-size: 12px;
    }
    .check-icon {
      color: #22c55e;
      font-size: 26px;
    }
    .button {
      width: 100%;
      padding: 16px;
      background: linear-gradient(135deg, #44C2FD 0%, #8B5CF6 100%);
      color: white;
      border: none;
      border-radius: 12px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 8px 24px rgba(68, 194, 253, 0.3);
      letter-spacing: 0.02em;
    }
    .button:hover {
      transform: translateY(-2px);
      box-shadow: 0 12px 32px rgba(68, 194, 253, 0.4);
    }
    .button:disabled {
      opacity: 0.4;
      cursor: not-allowed;
      transform: none;
      box-shadow: 0 4px 12px rgba(68, 194, 253, 0.2);
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>${event.params.title || 'Document Upload'}</h1>
      <div class="case-badge">Case ${event.params.caseId || 'SC-XXXXXX'}</div>
    </div>
    <div class="content">
      <div class="description">
        ${event.params.description || 'Please upload the required document to proceed with your request.'}
      </div>
      
      <div id="uploadArea" class="upload-area" onclick="simulateUpload()">
        <div class="upload-icon">📄</div>
        <div class="upload-text">Click to upload document</div>
        <div class="upload-hint">PDF, JPG, or PNG (max 10MB)</div>
      </div>
      
      <div id="fileUploaded" class="file-uploaded" style="display: none;">
        <div class="file-icon">📄</div>
        <div class="file-info">
          <div class="file-name">${event.params.fileName || 'document.pdf'}</div>
          <div class="file-size">2.4 MB • Uploaded successfully</div>
        </div>
        <div class="check-icon">✓</div>
      </div>
      
      <button id="submitButton" class="button" disabled onclick="submitForm()">
        Submit Document
      </button>
    </div>
  </div>
  
  <script>
    function simulateUpload() {
      const uploadArea = document.getElementById('uploadArea');
      const fileUploaded = document.getElementById('fileUploaded');
      const submitButton = document.getElementById('submitButton');
      
      uploadArea.style.display = 'none';
      fileUploaded.style.display = 'flex';
      submitButton.disabled = false;
    }
    
    function submitForm() {
      // In demo, this is handled by the script
      alert('Document submitted successfully!');
    }
  </script>
</body>
</html>
        `;
        const dataUrl = 'data:text/html;charset=utf-8,' + encodeURIComponent(formHtml);
        dispatch(setAppUrl(dataUrl));
        break;

      case 'reset':
        // Clear typing text
        setIsPlaying(false);
        onTypingTextChange('');
        if (typingIntervalRef.current) {
          clearInterval(typingIntervalRef.current);
          typingIntervalRef.current = null;
        }
        
        // Trigger reset animation
        onResetStart();
        
        // Reset to beginning of script without full page reload (preserves auth pane state)
        setTimeout(() => {
          setCurrentEventIndex(0);  // Reset script to beginning
          setIsPlaying(true);       // Continue playing
        }, 1000);
        break;

      case 'wait':
        // Just wait, no action
        break;

      default:
        console.warn('⚠️ Unknown event type:', event.type);
    }
  };

  // Script player
  useEffect(() => {
    if (!script || !isPlaying || currentEventIndex >= script.events.length) {
      if (currentEventIndex >= script?.events.length && isPlaying) {
        // Script finished, restart after a delay
        timeoutRef.current = setTimeout(() => {
          // Reset to beginning instead of reloading
          setCurrentEventIndex(0);
          setIsPlaying(true);
        }, 3000);
      }
      return;
    }

    const event = script.events[currentEventIndex];
    
    timeoutRef.current = setTimeout(() => {
      processEvent(event);
      setCurrentEventIndex(prev => prev + 1);
    }, event.delay || 0);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [script, isPlaying, currentEventIndex]);

  return null; // This component only orchestrates, doesn't render
};

// Wrapper for AgentView that simulates typing
const DemoAgentView = () => {
  const { typingText } = useContext(DemoTypingContext);
  
  // Use a wrapper div with the AgentView and inject typing
  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      <AgentView />
      {typingText && (
        <div style={{
          position: 'absolute',
          bottom: '0',
          left: '0',
          right: '0',
          zIndex: 1001,
          // Match the footer-glass structure exactly
          padding: '10px 0',
        }}>
          <div style={{
            // Match the inner container with px-4 and gap-2
            paddingLeft: '16px',
            paddingRight: '16px',
            display: 'flex',
            gap: '8px', // gap-2
            alignItems: 'flex-start' // Align to top, not stretch
          }}>
            {/* Typing overlay that matches textarea exactly */}
            <div style={{
              flex: 1,
              background: 'rgba(30, 41, 59, 0.8)', // Match slate-800 bg-opacity-80
              color: 'rgb(241, 245, 249)', // Match slate-100
              padding: '8px 16px', // Match py-2 px-4
              borderRadius: '12px', // Match rounded-xl
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              fontSize: '15px',
              lineHeight: '1.5',
              border: '1px solid rgb(71, 85, 105)', // Match slate-600
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
              height: '94px', // Fixed height to match button stack: 44px + 6px gap + 44px
              overflow: 'hidden',
              display: 'block', // Block layout like textarea
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'pre-wrap', // Allow text to wrap naturally
            }}>
              {typingText}
              <span style={{ 
                animation: 'blink 1s infinite',
                opacity: 0.7,
              }}>|</span>
            </div>
            {/* Spacer to match button stack */}
            <div style={{ 
              width: '44px', 
              height: '94px', // Match typing box height
              flexShrink: 0 
            }} />
          </div>
        </div>
      )}
    </div>
  );
};

// Main device UI component
const DemoPhoneUI = ({ channelTransitioning }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const currentChannel = useSelector((state) => state.channel.currentChannel);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const bezelColor = '#1a1a2e'; // Dark iPhone-like bezel color
  
  // Animation styles based on transition state
  const channelAnimationStyle = channelTransitioning === 'opening' ? {
    animation: 'appOpen 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
  } : channelTransitioning === 'closing' ? {
    animation: 'appClose 0.3s cubic-bezier(0.4, 0.0, 1, 1)',
  } : {};

  // Render the appropriate channel component
  const renderChannel = () => {
    switch (currentChannel) {
      case 'home':
        return <HomeView onAppOpen={() => {}} />;
      case 'agent':
        return <DemoAgentView />;
      case 'sms':
        return <MessagingView />;
      case 'email':
        return <EmailView />;
      case 'calendar':
        return <CalendarView />;
      case 'app':
        return <AppView />;
      default:
        return <HomeView onAppOpen={() => {}} />;
    }
  };

  return (
    <div 
      className="device-bezel is-iphone flex flex-col items-center justify-center"
      style={{
        width: '50vh',
        height: '90vh',
        maxHeight: '850px',
        maxWidth: '420px',
        borderRadius: '60px',
        padding: '8px',
        background: createBezelGradient(bezelColor),
        boxShadow: '0 25px 50px -15px rgba(0,0,0,0.3), 0 8px 32px rgba(0,0,0,0.15), inset 0 2px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.1)',
        border: '1px solid rgba(255,255,255,0.8)',
        pointerEvents: 'none',
        boxSizing: 'border-box',
        transition: 'all 0.6s cubic-bezier(0.4, 0.0, 0.2, 1)',
      }}
    >
      {/* Device Screen */}
      <div 
        className="device-screen bg-black w-full h-full flex flex-col rounded-[48px] select-none text-[11px] relative overflow-hidden"
        style={{
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.05)',
          transition: 'all 0.6s cubic-bezier(0.4, 0.0, 0.2, 1)',
        }}
      >
        {/* Notch */}
        <div 
          className="absolute top-0 left-1/2 -translate-x-1/2 bg-black z-20"
          style={{
            width: '140px',
            height: '28px',
            borderBottomLeftRadius: '16px',
            borderBottomRightRadius: '16px',
          }}
        >
          <div className="absolute top-7 left-1/2 -translate-x-1/2 w-12 h-1 bg-gray-800 rounded-full" />
          <div className="absolute top-6 left-1/2 translate-x-4 w-2.5 h-2.5 bg-gray-900 rounded-full border border-gray-700" />
        </div>

        {/* Status Bar */}
        <div 
          className="status-bar h-12 flex items-center justify-between px-6 pt-3 text-sm font-medium z-20 text-white relative" 
          style={{ 
            minHeight: '48px', 
            background: 'rgba(0,0,0,0.1)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            position: 'relative',
            paddingTop: '12px',
          }}
        >
          <div className="flex items-center space-x-3">
            <span className="font-semibold">
              {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            {/* Signal strength */}
            <div className="flex items-end space-x-0.5" style={{ height: '12px' }}>
              {[1, 2, 3, 4].map((bar) => (
                <div
                  key={bar}
                  style={{
                    width: '3px',
                    height: `${bar * 3}px`,
                    backgroundColor: 'white',
                    borderRadius: '1px',
                  }}
                />
              ))}
            </div>
            {/* Battery */}
            <div className="flex items-center space-x-1">
              <div
                style={{
                  width: '22px',
                  height: '11px',
                  border: '1.5px solid white',
                  borderRadius: '3px',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '1.5px',
                }}
              >
                <div style={{ width: '100%', height: '100%', backgroundColor: 'white', borderRadius: '1.5px' }} />
              </div>
              <div style={{ width: '1.5px', height: '5px', backgroundColor: 'white', borderRadius: '0 1px 1px 0' }} />
            </div>
          </div>
        </div>

        {/* Notifications */}
        <NotificationContainer />

        {/* Channel Content */}
        <div 
          className="flex-1 flex flex-col relative overflow-hidden"
          style={channelAnimationStyle}
        >
          {renderChannel()}
        </div>

        {/* Footer with Home Button */}
        <div 
          className="relative bg-black flex items-center justify-center"
          style={{
            borderRadius: '0 0 48px 48px',
            background: '#000',
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <div
            className="rounded-full flex items-center justify-center w-10 h-10"
            style={{
              background: createHomeButtonGradient(bezelColor),
              boxShadow: '0 4px 12px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -1px 0 rgba(0,0,0,0.5)',
              border: '1px solid rgba(0,0,0,0.3)'
            }}
          >
            <span 
              className="material-icons text-lg"
              style={{ 
                color: '#e5e7eb',
                textShadow: '0 1px 2px rgba(0,0,0,0.8)'
              }}
            >
              home
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const RESET = {
  type: 'demo/reset'
}

// Wrapper to prevent localStorage operations in demo mode
const createDemoReducer = (reducer) => {
  return (state, action) => {
    // Save original localStorage methods
    const originalSetItem = localStorage.setItem;
    const originalGetItem = localStorage.getItem;
    const originalRemoveItem = localStorage.removeItem;
    
    // Disable localStorage during demo reducer operations
    localStorage.setItem = () => {};
    localStorage.getItem = () => null;
    localStorage.removeItem = () => {};
    
    // Run the reducer
    const newState = reducer(state, action);
    
    // Restore localStorage methods
    localStorage.setItem = originalSetItem;
    localStorage.getItem = originalGetItem;
    localStorage.removeItem = originalRemoveItem;
    
    return newState;
  };
};

// Wrapper component that provides isolated Redux store
const DemoPhone = ({ scriptUrl = '/demo-script.json' }) => {
  const [typingText, setTypingText] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [channelTransitioning, setChannelTransitioning] = useState(null);
  
  const store = useRef(null);
  if (store.current === null) {
    // make sure only single store is initialized
    const appReducer = combineReducers({
        channel: createDemoReducer(channelReducer),
        device: createDemoReducer(deviceReducer),
        notifications: createDemoReducer(notificationReducer),
        sidePanel: createDemoReducer(sidePanelReducer)
    });
    store.current = configureStore({
      reducer: (state, action) => appReducer(
        action.type === RESET.type ? createInitialState() : state, 
        action
      ),
      preloadedState: createInitialState(),
    })
  }

  const handleResetStart = () => {
    setIsResetting(true);
    setToasts([]); // Clear toasts on reset
    store.current.dispatch(RESET); // Reset redux store
    setTimeout(() => setIsResetting(false), 1500);
  };

  const handleChannelTransition = (channel, type) => {
    setChannelTransitioning(type);
    // Clear animation state after animation completes
    setTimeout(() => setChannelTransitioning(null), type === 'opening' ? 400 : 300);
  };

  const addToast = (toast) => {
    const id = Date.now();
    setToasts(prev => [...prev, { ...toast, id }]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  return (
    <Provider store={store.current}>
      <DemoTypingContext.Provider value={{ typingText, setTypingText }}>
        <DemoToastContext.Provider value={{ toasts, addToast, removeToast }}>
        <style>{`
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0; }
          }
          @keyframes demoFadeOut {
            0% { opacity: 1; }
            100% { opacity: 0.3; }
          }
          @keyframes demoFadeIn {
            0% { opacity: 0.3; }
            100% { opacity: 1; }
          }
          @keyframes slideInFromPhone {
            0% {
              transform: translateX(-120px);
              opacity: 0;
            }
            100% {
              transform: translateX(0);
              opacity: 1;
            }
          }
          @keyframes appOpen {
            0% {
              opacity: 0;
              transform: scale(0.9);
            }
            100% {
              opacity: 1;
              transform: scale(1);
            }
          }
          @keyframes appClose {
            0% {
              opacity: 1;
              transform: scale(1);
            }
            100% {
              opacity: 0;
              transform: scale(1.05);
            }
          }
        `}</style>
        <div style={{
          position: 'relative',
          display: 'inline-block'
        }}>
          <div style={{
            animation: isResetting ? 'demoFadeOut 0.5s ease-out, demoFadeIn 0.5s 1s ease-in' : 'none',
          }}>
            <DemoOrchestrator 
              scriptUrl={scriptUrl} 
              onTypingTextChange={setTypingText}
              onResetStart={handleResetStart}
              onChannelTransition={handleChannelTransition}
            />
            <DemoPhoneUI channelTransitioning={channelTransitioning} />
          </div>
          {/* Reset overlay with pulse effect */}
          {isResetting && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 9999,
              pointerEvents: 'none'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'rgba(68, 194, 253, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'pulse 1s ease-in-out infinite'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'rgba(68, 194, 253, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: 'rgba(68, 194, 253, 0.8)'
                  }} />
                </div>
              </div>
            </div>
          )}
          {/* Toast notifications sliding out from phone - always positioned to the right on all screen sizes */}
          <div className="toast-container" style={{
            position: 'absolute',
            left: '100%', // Position right after the phone
            bottom: '85px', // 85px from bottom of device bezel
            zIndex: 50,
            marginLeft: '20px', // Gap between phone and toasts
            width: '300px',
            pointerEvents: 'none', // Don't block interactions
            textAlign: 'left', // Ensure text is left-aligned
          }}>
            <style>{`
              /* Toast container stays to the right on all screen sizes */
              .toast-container {
                left: 100%;
                margin-left: 20px;
                position: absolute;
              }
              
              /* On mobile, reduce margin slightly if needed */
              @media (max-width: 768px) {
                .toast-container {
                  margin-left: 10px;
                  width: 250px;
                }
              }
            `}</style>
            {toasts.map((toast, index) => (
              <div
                key={toast.id}
                style={{
                  marginBottom: '12px',
                  width: '100%',
                  position: 'relative',
                  pointerEvents: 'auto',
                }}
              >
                {/* Wrapper div that doesn't get affected by ToastNotification's internal animations */}
                <div style={{
                  animation: 'slideInFromPhone 0.5s ease-out forwards',
                  animationFillMode: 'both',
                }}>                <ToastNotification
                  subject={toast.subject}
                  body={toast.body}
                  icon={toast.icon}
                  onClose={() => removeToast(toast.id)}
                  stackIndex={0}
                  totalToasts={1}
                  insideContainer={true}
                  hideClose={true}
                  lightTheme={true}
                />
                </div>
              </div>
            ))}
          </div>
        </div>
        </DemoToastContext.Provider>
      </DemoTypingContext.Provider>
    </Provider>
  );
};

export default DemoPhone;
